import { SelectionModel } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import {
  AutocompleteOption,
  GroupedAutocompleteOptions,
} from '../../../shared/dto/grouped-autocomplete-options.model';
import { ServerSideDataSource } from '../../../shared/abstract-classes/serverSideDataSource';

import { EmployeesAllData, ListEmployeeModel } from '../models/employee.model';
import { EmployeesDataService } from './employees-data.service';

export class EmployeesDataSource extends ServerSideDataSource<ListEmployeeModel> {
  protected loadingSubject = new BehaviorSubject<boolean>(false);
  protected itemsSubject = new BehaviorSubject<ListEmployeeModel[]>([]);

  private autocompleteSearch$ = new Subject<{
    search: string;
    existingFilters: AutocompleteOption[];
  }>();
  readonly autocompleteOptions$: Observable<GroupedAutocompleteOptions[]> = this.autocompleteSearch$
    .asObservable()
    .pipe(
      switchMap(({ search, existingFilters }) =>
        this.employeesDataService.getSuggestionsList(search).pipe(
          this.recoverFromError(),
          map(groupedOptions =>
            groupedOptions
              .map(group => {
                group.categories = group.categories.filter(
                  o => !existingFilters.find(f => f.category === o.category && f.value === o.value)
                );
                return group;
              })
              .filter(group => group.categories.length)
          )
        )
      )
    );
  private _filters$ = new Subject();

  selection = new SelectionModel<ListEmployeeModel>(true, []);
  loading$ = this.loadingSubject.asObservable();
  data: ListEmployeeModel[] = [];
  totalElements = 0;
  isOnInexistentPage: boolean;

  constructor(
    protected employeesDataService: EmployeesDataService,
    public sort: MatSort,
    public paginator: MatPaginator
  ) {
    super(sort, paginator);

    this._filters$
      .pipe(
        switchMap(params =>
          this.employeesDataService.getListData(params).pipe(this.recoverFromError())
        ),
        tap(({ employees: { totalElements, number, totalPages } }: EmployeesAllData) => {
          this.totalElements = totalElements;
          this.isOnInexistentPage = number ? number + 1 > totalPages : false;
        }),
        map((entitiesAllData: EmployeesAllData) => entitiesAllData.employees.content),
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe((entities: ListEmployeeModel[]) => {
        this.setData(entities);
      });
  }

  getAll(): void {
    this.selection.clear();
    this.loadingSubject.next(true);

    const params = {
      sort: `${this.sort?.active || 'name'}:${this.sort?.direction || 'asc'}`,
      size: this.paginator.pageSize || this.PAGE_SIZE,
      page: this.paginator.pageIndex || 0,
      ...this.filters,
    };

    this._filters$.next(params);
  }

  loadAutocompleteOptions(search: string, existingFilters: AutocompleteOption[] = []): void {
    this.autocompleteSearch$.next({ search, existingFilters });
  }
}
